import { addDays, formatDate, londonDate, londonTime } from '@walkkit/shared';
import { s } from '../strings';

/** "Mon 5 Oct" for a date string, tolerating null. */
export function fmtDate(d: string | null | undefined, fallback = '—'): string {
  return d ? formatDate(d) : fallback;
}

const longDate = new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
/** "Monday 5 October 2026" */
export function fmtLongDate(d: string): string {
  return longDate.format(new Date(`${d}T00:00:00Z`));
}

const shortDate = new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', day: 'numeric', month: 'short', year: 'numeric' });
/** "5 Oct 2026" */
export function fmtDateYear(d: string | null | undefined, fallback = '—'): string {
  return d ? shortDate.format(new Date(`${d}T00:00:00Z`)) : fallback;
}

const dayMonth = new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', day: 'numeric', month: 'short' });
/** "26 Oct" */
export function fmtDayMonth(d: string): string {
  return dayMonth.format(new Date(`${d}T00:00:00Z`));
}

const monthYear = new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', month: 'long', year: 'numeric' });
export function fmtMonth(d: string): string {
  return monthYear.format(new Date(`${d}T00:00:00Z`));
}

/** "14:05" London time for an ISO instant. */
export function fmtTime(iso: string | null | undefined, fallback = '—'): string {
  return iso ? londonTime(new Date(iso)) : fallback;
}

/** "Mon 5 Oct, 14:05" London; "Today, 14:05" / "Yesterday, 14:05" when recent. */
export function fmtDateTime(iso: string | null | undefined, fallback = '—'): string {
  if (!iso) return fallback;
  const at = new Date(iso);
  const day = londonDate(at);
  const today = londonDate();
  const label = day === today ? s.common.today : day === addDays(today, -1) ? s.common.yesterday : formatDate(day);
  return `${label}, ${londonTime(at)}`;
}

export function initials(name: string | null | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '')).toUpperCase() || '?';
}

/** Whole years and months between a dob and today. */
export function ageParts(dob: string, today: string): { years: number; months: number } {
  const [y1, m1, d1] = dob.split('-').map(Number) as [number, number, number];
  const [y2, m2, d2] = today.split('-').map(Number) as [number, number, number];
  let months = (y2 - y1) * 12 + (m2 - m1) - (d2 < d1 ? 1 : 0);
  if (months < 0) months = 0;
  return { years: Math.floor(months / 12), months: months % 12 };
}
